import React, { useMemo, useState } from 'react';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SubmitButton from '../components/forms/formComponents/SubmitButton';
import ErrorMessage from '../components/forms/formComponents/ErrorMessage';
import { Card } from '../components/ui/card';
import { auth } from '../firebase-client';
import { getFunctionUrl } from '../utils/functionURLs';
import { getCurrencyMeta } from '../utils/currency';
import { reportCaughtError } from '../utils/clientErrorReporting';

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

function SubscriptionCheckoutForm({ stripeLoadError = '' }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const teamId = searchParams.get('team');
  const clientSecret = searchParams.get('clientSecret');
  const setupIntentClientSecret = searchParams.get('setupIntentClientSecret');
  const subscriptionId = searchParams.get('subscriptionId');
  const paymentIntentId = searchParams.get('paymentIntentId') || '';
  const setupIntentId = searchParams.get('setupIntentId') || '';
  const requiresSubscriptionFinalization = searchParams.get('requiresSubscriptionFinalization') === 'true';
  const prelaunchDeferred = searchParams.get('prelaunchDeferred') === 'true';

  // Read breakdown and total
  const firstChargeBaseAmount = parseInt(searchParams.get('firstChargeBaseAmount'), 10) || 0;
  const firstChargePlatformFee = parseInt(searchParams.get('firstChargePlatformFee'), 10) || 0;
  const firstChargeStripeFee = parseInt(searchParams.get('firstChargeStripeFee'), 10) || 0;
  const baseAmount = parseInt(searchParams.get('baseAmount'), 10) || 0;
  const platformFee = parseInt(searchParams.get('platformFee'), 10) || 0;
  const stripeFee = parseInt(searchParams.get('stripeFee'), 10) || 0;
  const rawTotal = searchParams.get('finalAmount') ?? searchParams.get('amount');
  const finalAmount = rawTotal ? parseInt(rawTotal, 10) : null;
  const firstChargeAmount = parseInt(searchParams.get('firstChargeAmount'), 10) || null;
  const membershipYearAnchor = searchParams.get('membershipYearAnchor') || '';
  const currency = searchParams.get('currency') || 'GBP';
  const learnToRowCreditAppliedMinor = parseInt(searchParams.get('learnToRowCreditAppliedMinor'), 10) || 0;
  const membershipDiscountAppliedMinor = parseInt(searchParams.get('membershipDiscountAppliedMinor'), 10) || 0;
  const membershipStoredCreditAppliedMinor = parseInt(searchParams.get('membershipStoredCreditAppliedMinor'), 10) || 0;
  const membershipStoredCreditRemainingMinor = parseInt(searchParams.get('membershipStoredCreditRemainingMinor'), 10) || 0;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [finalizationPaymentIntentId, setFinalizationPaymentIntentId] = useState('');
  const [finalizationSetupIntentId, setFinalizationSetupIntentId] = useState('');
  const stripe = useStripe();
  const elements = useElements();
  const visibleError = error || stripeLoadError;
  const showsDifferentFirstCharge = firstChargeAmount != null && finalAmount != null && firstChargeAmount !== finalAmount;
  const format = minor => {
    const {
      symbol,
      decimals
    } = getCurrencyMeta(currency);
    const major = Number(minor || 0) / Math.pow(10, decimals);
    return `${symbol}${major.toFixed(decimals)}`;
  };
  const cancelIncompleteSubscription = async () => {
    if (!subscriptionId && !paymentIntentId && !setupIntentId) return;
    const user = auth.currentUser;
    if (!user) return;
    try {
      const token = await user.getIdToken();
      await fetch(getFunctionUrl('cancelIncompleteSubscription'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          subscriptionId,
          paymentIntentId,
          setupIntentId
        })
      });
    } catch (cancelErr) {}
  };
  const finalizeAnchoredSubscription = async ({
    confirmedPaymentIntentId = '',
    confirmedSetupIntentId = ''
  }) => {
    const user = auth.currentUser;
    if (!user) throw new Error('You must be signed in to finish subscription setup.');
    const token = await user.getIdToken();
    const res = await fetch(getFunctionUrl(prelaunchDeferred ? 'finalizePrelaunchCommitteeAuthorization' : 'finalizeAnchoredSubscription'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        paymentIntentId: confirmedPaymentIntentId,
        setupIntentId: confirmedSetupIntentId
      })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || 'Membership payment succeeded, but the subscription could not be finalised.');
    }
    return data;
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    let paymentCompleted = false;
    try {
      let confirmedPaymentIntentId = finalizationPaymentIntentId;
      let confirmedSetupIntentId = finalizationSetupIntentId;
      if (!confirmedPaymentIntentId && !confirmedSetupIntentId) {
        if (stripeLoadError) throw new Error(stripeLoadError);
        if (!stripe || !elements) throw new Error('Stripe is still loading. Please try again in a moment.');
        const card = elements.getElement(CardElement);
        if (!card) throw new Error('Card element not found');
        const result = setupIntentClientSecret ? await stripe.confirmCardSetup(setupIntentClientSecret, {
          payment_method: {
            card
          }
        }) : await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card
          }
        });
        if (result.error) {
          throw new Error(result.error.message);
        }
        if (setupIntentClientSecret && (requiresSubscriptionFinalization || prelaunchDeferred)) {
          confirmedSetupIntentId = result?.setupIntent?.id || setupIntentId;
          if (!confirmedSetupIntentId) {
            throw new Error('Card setup succeeded, but the setup intent could not be resolved for subscription setup.');
          }
          setFinalizationSetupIntentId(confirmedSetupIntentId);
          paymentCompleted = true;
        } else if (!setupIntentClientSecret && requiresSubscriptionFinalization) {
          confirmedPaymentIntentId = result?.paymentIntent?.id || paymentIntentId;
          if (!confirmedPaymentIntentId) {
            throw new Error('Payment succeeded, but the payment intent could not be resolved for subscription setup.');
          }
          setFinalizationPaymentIntentId(confirmedPaymentIntentId);
          paymentCompleted = true;
        } else if (!setupIntentClientSecret) {
          paymentCompleted = true;
        }
      } else {
        paymentCompleted = true;
      }
      if (requiresSubscriptionFinalization || prelaunchDeferred) {
        await finalizeAnchoredSubscription({
          confirmedPaymentIntentId,
          confirmedSetupIntentId
        });
      }
      setSuccess(true);
      if (teamId) {
        navigate(prelaunchDeferred ? `/teams/${teamId}` : `/teams/${teamId}/payment-success`, { replace: true });
      }
    } catch (err) {
      if (!paymentCompleted) {
        await cancelIncompleteSubscription();
        setError(err.message || 'Payment failed');
      } else {
        setError(`${err.message || 'Payment succeeded, but subscription setup did not finish.'} Press the button again to retry.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Invalid link
  if (!clientSecret && !setupIntentClientSecret) {
    return <div style={{
      backgroundColor: '#f9fafb',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    }}>
        <Card title="Invalid Payment Link" style={{
        maxWidth: '400px'
      }}>
          <p>We couldn’t load your payment details. Please return to your club and try again.</p>
        </Card>
      </div>;
  }

  // Success state
  if (success) {
    return <div style={{
      backgroundColor: '#f9fafb',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    }}>
        <Card title={prelaunchDeferred ? 'Payment Authorised' : 'Payment Successful'} style={{
        maxWidth: '400px'
      }}>
          <p>{prelaunchDeferred
            ? 'Your membership details and card authorization are ready. Nothing will be charged until the club’s first launch attempt.'
            : setupIntentClientSecret ? 'Your payment method has been saved and your membership subscription is now set up.' : 'You’ve successfully subscribed to your club’s membership plan.'} You can now return to your club dashboard.</p>
          {prelaunchDeferred && searchParams.get('team') && (
            <a className="button button-primary" href={`/teams/${searchParams.get('team')}`}>Return to club</a>
          )}
          {!prelaunchDeferred && searchParams.get('team') && (
            <a className="button button-primary" href={`/teams/${searchParams.get('team')}`}>Return to club</a>
          )}
        </Card>
      </div>;
  }

  // Main form
  return <div style={{
    backgroundColor: '#f9fafb',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px'
  }}>
      <form onSubmit={handleSubmit} style={{
      width: '100%',
      maxWidth: '600px',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    }}>
        <Card title={prelaunchDeferred ? 'Authorise Launch Payment' : 'Complete Your Subscription'} style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        backgroundColor: '#ffffff',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>

          {/* Instructions */}
          <p style={{
          color: '#4b5563',
          lineHeight: '1.5'
        }}>
            {prelaunchDeferred ? 'Save the card Stripe should charge when the club first attempts to go live. No payment is taken today.' : setupIntentClientSecret ? 'To set up your membership subscription, please enter the card you want Stripe to charge for future renewals.' : 'To complete your membership payment, please enter your card details below.'}
            {' '}This is a secure payment handled by Stripe.
          </p>

          {/* Breakdown */}
          {finalAmount != null && <div style={{
          padding: '16px',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          backgroundColor: '#ffffff'
        }}>
              <ul style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            fontSize: '14px',
            color: '#4b5563'
          }}>
                {firstChargeAmount != null && <>
                    <li style={{
                marginBottom: '8px',
                fontSize: '12px',
                fontWeight: '700',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                color: '#6b7280'
              }}>
                      {setupIntentClientSecret ? 'Saved card / next charge' : 'Today'}
                    </li>
                    <li style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#111827'
              }}>
                      <span>{setupIntentClientSecret ? 'Due today:' : 'Charged today:'}</span>
                      <strong>{format(firstChargeAmount)}</strong>
                    </li>
                    {(showsDifferentFirstCharge || firstChargeBaseAmount > 0 || firstChargePlatformFee > 0 || firstChargeStripeFee > 0) && <>
                        <li style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px'
                }}>
                          <span>Membership:</span>
                          <strong>{format(firstChargeBaseAmount)}</strong>
                        </li>
                        {firstChargePlatformFee > 0 && <li style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}>
                            <span>Member Nest Fee:</span>
                            <strong>{format(firstChargePlatformFee)}</strong>
                          </li>}
                        {firstChargeStripeFee > 0 && <li style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}>
                            <span>Stripe Fee:</span>
                            <strong>{format(firstChargeStripeFee)}</strong>
                          </li>}
                      </>}
                  </>}
                <li style={{
              margin: firstChargeAmount != null ? '12px 0 8px' : '0 0 8px',
              paddingTop: firstChargeAmount != null ? '12px' : 0,
              borderTop: firstChargeAmount != null ? '1px solid #e5e7eb' : 'none',
              fontSize: '12px',
              fontWeight: '700',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              color: '#6b7280'
            }}>
                  Renewal
                </li>
                <li style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px'
            }}>
                  <span>Membership:</span>
                  <strong>{format(baseAmount)}</strong>
                </li>
                {platformFee > 0 && <li style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px'
            }}>
                    <span>Member Nest Fee:</span>
                    <strong>{format(platformFee)}</strong>
                  </li>}
                {stripeFee > 0 && <li style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px'
            }}>
                    <span>Stripe Fee:</span>
                    <strong>{format(stripeFee)}</strong>
                  </li>}
                <li style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '8px',
              fontWeight: '600',
              color: '#111827'
            }}>
                  <span>Renewal total:</span>
                  <strong>{format(finalAmount)}</strong>
                </li>
                {learnToRowCreditAppliedMinor > 0 && <li style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#6b7280'
            }}>
                    Course fee credit applied today: {format(learnToRowCreditAppliedMinor)}.
                  </li>}
                {membershipDiscountAppliedMinor > 0 && <li style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#6b7280'
            }}>
                    Membership discounts applied today: {format(membershipDiscountAppliedMinor)}.
                  </li>}
                {membershipStoredCreditAppliedMinor > 0 && <li style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#6b7280'
            }}>
                    Stored membership credit applied today: {format(membershipStoredCreditAppliedMinor)}.
                  </li>}
                {membershipStoredCreditRemainingMinor > 0 && <li style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#6b7280'
            }}>
                    Stored membership credit remaining after setup: {format(membershipStoredCreditRemainingMinor)}.
                  </li>}
                {membershipYearAnchor && <li style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#6b7280'
            }}>
                    Renews on {new Date(membershipYearAnchor).toLocaleDateString('en-GB')} at {format(finalAmount)}.
                  </li>}
              </ul>
            </div>}

          {/* Card details */}
          <div style={{
          marginTop: '24px',
          padding: '16px',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          backgroundColor: '#ffffff'
        }}>
            <CardElement options={{
            hidePostalCode: true
          }} />
          </div>

          {visibleError && <ErrorMessage message={visibleError} />}

          {/* Submit */}
          <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '32px' /* ← add extra space above the button */
        }}>
              <SubmitButton
                label={submitting ? 'Processing…' : (finalizationPaymentIntentId || finalizationSetupIntentId) ? 'Finish Setup' : setupIntentClientSecret ? 'Save Card' : 'Confirm Payment'}
                loading={submitting}
                primary
                disabled={Boolean(stripeLoadError) || (!stripe && !finalizationPaymentIntentId && !finalizationSetupIntentId) || submitting}
              />
            </div>
        </Card>
      </form>
    </div>;
}

export default function SubscriptionCheckout() {
  const [stripeLoadError, setStripeLoadError] = useState(stripeKey ? '' : 'Payments are not configured. Please contact support.');
  const stripePromise = useMemo(() => {
    if (!stripeKey) return Promise.resolve(null);

    return loadStripe(stripeKey).catch((err) => {
      setStripeLoadError('We could not load the secure payment form. Please check your connection, disable browser blockers for this page, then refresh.');
      reportCaughtError(err, {
        source: 'stripe.loadStripe',
        severity: 'warning',
        context: {
          path: typeof window !== 'undefined' ? window.location.pathname : '',
        },
      });
      return null;
    });
  }, []);

  return (
    <Elements stripe={stripePromise}>
      <SubscriptionCheckoutForm stripeLoadError={stripeLoadError} />
    </Elements>
  );
}
